import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FilmIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  PlayCircleIcon,
  ArrowUpTrayIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Label from '../ui/Label';
import Select from '../ui/Select';
import Modal from '../ui/Modal';
import { ModalActions } from '../ui/ModalFooter';
import SegmentedControl from '../ui/SegmentedControl';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import { getApiErrorMessage } from '../../utils/apiError';
import VideoPlayer, { resolveMediaUrl } from './VideoPlayer';
import { validateFields, validateRequired } from '../../utils/formValidation';
import { useRequestedVideo } from './useRequestedVideo';

const EMPTY_FORM = {
  title: '',
  description: '',
  category: 'general',
  source_type: 'url',
  external_url: '',
  visible_to_clients: true,
  visible_to_vendors: false,
  is_published: true,
  is_featured: false,
  sort_order: 0,
  thumbnail_url: '',
  links: [{ label: '', url: '' }],
};

const CATEGORIES = ['general', 'onboarding', 'training', 'product', 'announcements'];
const SORT_PRESETS = [
  { value: 0, labelKey: 'sortFirst' },
  { value: 10, labelKey: 'sortHigh' },
  { value: 50, labelKey: 'sortNormal' },
  { value: 100, labelKey: 'sortLast' },
];

function audienceValue(form) {
  if (form.visible_to_clients && form.visible_to_vendors) return 'all';
  if (form.visible_to_vendors) return 'vendors';
  return 'clients';
}

function listingValue(form) {
  if (!form.is_published) return 'draft';
  if (form.is_featured) return 'featured';
  return 'published';
}

function companyRequestConfig(companyId) {
  return companyId ? { params: { company_id: companyId } } : {};
}

function VideoHubAdmin({ presetCompanyId = null }) {
  const { t, formatMessage } = useLanguage();
  const { user } = useAuth();
  const { confirm, dialog } = useConfirmDialog();
  const vh = t.videoHub;
  const isPlatformAdmin = user?.is_admin && !user?.company_id;
  const activeCompanyId = user?.company_id || presetCompanyId || null;
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const fileInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const fetchVideos = useCallback(async () => {
    if (isPlatformAdmin && !activeCompanyId) {
      setVideos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/videos', {
        withCredentials: true,
        ...companyRequestConfig(activeCompanyId),
      });
      setVideos(response.data.videos || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, vh.loadError));
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, formatMessage, isPlatformAdmin, t, vh.loadError]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const openPreview = useCallback((video) => {
    setPreviewVideo(video);
  }, []);

  const onMissingVideo = useCallback(() => {
    toast.error(vh.videoUnavailable);
  }, [vh.videoUnavailable]);

  useRequestedVideo({
    items: videos,
    loading,
    onOpen: openPreview,
    onMissing: onMissingVideo,
  });

  const stats = useMemo(() => ({
    total: videos.length,
    published: videos.filter((video) => video.is_published).length,
    clients: videos.filter((video) => video.visible_to_clients).length,
    vendors: videos.filter((video) => video.visible_to_vendors).length,
  }), [videos]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFile(null);
    setThumbnailFile(null);
    setFieldErrors({});
    setModalOpen(true);
  };

  const openEdit = (video) => {
    setEditing(video);
    setForm({
      title: video.title || '',
      description: video.description || '',
      category: video.category || 'general',
      source_type: video.source_type || 'url',
      external_url: video.external_url || '',
      visible_to_clients: Boolean(video.visible_to_clients),
      visible_to_vendors: Boolean(video.visible_to_vendors),
      is_published: Boolean(video.is_published),
      is_featured: Boolean(video.is_featured),
      sort_order: video.sort_order || 0,
      thumbnail_url: video.thumbnail_filename ? '' : (video.thumbnail_url || ''),
      links: video.links?.length ? video.links : [{ label: '', url: '' }],
    });
    setFile(null);
    setThumbnailFile(null);
    setFieldErrors({});
    setModalOpen(true);
  };

  const updateLink = (index, key, value) => {
    setForm((prev) => {
      const links = [...prev.links];
      links[index] = { ...links[index], [key]: value };
      return { ...prev, links };
    });
  };

  const buildPayload = () => {
    const links = (form.links || []).filter((link) => link.label.trim() && link.url.trim());
    const hasFile = Boolean(file);
    const useMultipart = hasFile || Boolean(thumbnailFile);
    const body = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      source_type: hasFile ? 'file' : form.source_type,
      external_url: form.external_url.trim(),
      visible_to_clients: form.visible_to_clients,
      visible_to_vendors: form.visible_to_vendors,
      is_published: form.is_published,
      is_featured: form.is_featured,
      sort_order: Number(form.sort_order) || 0,
      thumbnail_url: form.thumbnail_url.trim(),
      links,
    };
    if (!useMultipart) return { json: body, formData: null };
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (key === 'links') {
        formData.append('links', JSON.stringify(value));
      } else if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
      } else if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });
    if (file) formData.append('file', file);
    if (thumbnailFile) formData.append('thumbnail', thumbnailFile);
    return { json: null, formData };
  };

  const saveVideo = async () => {
    const { errors } = validateFields({
      title: validateRequired(form.title, vh.titleRequired),
    });
    if (form.source_type === 'url' && !file && !form.external_url.trim()) {
      errors.external_url = vh.urlRequired;
    }
    if (!form.visible_to_clients && !form.visible_to_vendors) {
      errors.audience = vh.audienceRequired;
    }
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    setSaving(true);
    try {
      const { json, formData } = buildPayload();
      const config = {
        withCredentials: true,
        ...companyRequestConfig(activeCompanyId),
      };
      if (editing) {
        await axios.put(`/api/admin/videos/${editing.id}`, formData || json, config);
        toast.success(vh.updated);
      } else {
        await axios.post('/api/admin/videos', formData || json, config);
        toast.success(vh.created);
      }
      setModalOpen(false);
      fetchVideos();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, vh.saveError));
    } finally {
      setSaving(false);
    }
  };

  const deleteVideo = async (video) => {
    const ok = await confirm({
      title: vh.deleteTitle,
      message: formatMessage(vh.deleteMessage, { title: video.title }),
      confirmLabel: vh.deleteConfirm,
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/admin/videos/${video.id}`, {
        withCredentials: true,
        ...companyRequestConfig(activeCompanyId),
      });
      toast.success(vh.deleted);
      fetchVideos();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, vh.saveError));
    }
  };

  const audienceLabel = (video) => {
    if (video.visible_to_clients && video.visible_to_vendors) return vh.audienceBoth;
    if (video.visible_to_vendors) return vh.audienceVendors;
    return vh.audienceClients;
  };

  if (loading) return <ListPageSkeleton />;

  return (
    <div className="space-y-5 sm:space-y-8">
      <PageHeader
        icon={FilmIcon}
        eyebrow={vh.adminEyebrow}
        title={vh.adminTitle}
        subtitle={vh.adminSubtitle}
        action={
          <Button className="w-full lg:w-auto" onClick={openCreate} icon={PlusIcon} disabled={isPlatformAdmin && !activeCompanyId}>
            {vh.addVideo}
          </Button>
        }
      />

      {isPlatformAdmin && !activeCompanyId ? (
        <EmptyState icon={FilmIcon} title={vh.pickCompanyTitle} description={vh.pickCompanyDescription} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { label: vh.statTotal, value: stats.total },
              { label: vh.statPublished, value: stats.published },
              { label: vh.statClients, value: stats.clients },
              { label: vh.statVendors, value: stats.vendors },
            ].map((stat) => (
              <div key={stat.label} className="card-premium p-3 sm:p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500 break-words sm:text-xs">{stat.label}</p>
                <p className="mt-1 text-xl font-bold text-secondary-900  sm:text-2xl">{stat.value}</p>
              </div>
            ))}
          </div>

          {videos.length === 0 ? (
            <EmptyState
              icon={PlayCircleIcon}
              title={vh.adminEmptyTitle}
              description={vh.adminEmptyDescription}
              action={<Button onClick={openCreate} icon={PlusIcon}>{vh.addVideo}</Button>}
            />
          ) : (
            <div className="space-y-3">
              {videos.map((video) => {
                const thumb = resolveMediaUrl(video.thumbnail_url || video.playback?.thumbnail_url);
                return (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card-premium flex flex-col gap-4 p-3 sm:flex-row sm:items-center sm:p-4"
                  >
                    <button
                      type="button"
                      onClick={() => setPreviewVideo(video)}
                      className="relative aspect-video w-full overflow-hidden rounded-2xl bg-secondary-900 sm:aspect-auto sm:h-24 sm:w-40 sm:shrink-0"
                    >
                      {thumb ? (
                        <img src={thumb} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="grid h-full place-items-center text-white">
                          <PlayCircleIcon className="h-8 w-8" />
                        </span>
                      )}
                    </button>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="min-w-0 break-words text-base font-bold text-secondary-900 ">
                          {video.title}
                        </h3>
                        {!video.is_published ? (
                          <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-secondary-600  ">
                            {vh.draft}
                          </span>
                        ) : null}
                        {video.is_featured ? (
                          <span className="shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-orange-700  ">
                            {vh.featured}
                          </span>
                        ) : null}
                      </div>
                      <p className="break-words text-sm text-secondary-500">
                        {vh.categories[video.category] || video.category} · {audienceLabel(video)} · {video.source_type === 'file' ? vh.sourceFile : vh.sourceUrl}
                      </p>
                      {video.description ? (
                        <p className="line-clamp-2 text-sm text-secondary-600 ">{video.description}</p>
                      ) : null}
                    </div>
                    <div className="grid w-full grid-cols-3 gap-2 sm:flex sm:w-auto sm:flex-col">
                      <Button className="min-h-[44px] px-2 sm:min-h-0 sm:px-3" size="small" variant="secondary" icon={PlayCircleIcon} onClick={() => setPreviewVideo(video)}>
                        {vh.preview}
                      </Button>
                      <Button className="min-h-[44px] px-2 sm:min-h-0 sm:px-3" size="small" variant="secondary" icon={PencilIcon} onClick={() => openEdit(video)}>
                        {vh.edit}
                      </Button>
                      <Button className="min-h-[44px] px-2 sm:min-h-0 sm:px-3" size="small" variant="danger" icon={TrashIcon} onClick={() => deleteVideo(video)}>
                        {vh.delete}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editing ? vh.editVideo : vh.addVideo}
        size="xl"
        footer={(
          <ModalActions
            onCancel={() => setModalOpen(false)}
            onConfirm={saveVideo}
            loading={saving}
            confirmLabel={editing ? vh.saveChanges : vh.createVideo}
          />
        )}
      >
        <div className="space-y-5">
          <div>
            <Label htmlFor="video-title" required>{vh.fieldTitle}</Label>
            <Input
              id="video-title"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              error={Boolean(fieldErrors.title)}
            />
            {fieldErrors.title ? <p className="mt-1 text-xs text-error-500">{fieldErrors.title}</p> : null}
          </div>
          <div>
            <Label htmlFor="video-description">{vh.fieldDescription}</Label>
            <Input
              id="video-description"
              as="textarea"
              rows={4}
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{vh.fieldCategory}</Label>
              <Select
                value={form.category}
                onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
              >
                {CATEGORIES.map((value) => (
                  <option key={value} value={value}>{vh.categories[value]}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="video-sort">{vh.fieldSortOrder}</Label>
              <Select
                id="video-sort"
                value={String(form.sort_order)}
                onChange={(event) => setForm((prev) => ({ ...prev, sort_order: Number(event.target.value) }))}
              >
                {SORT_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>{vh[preset.labelKey]}</option>
                ))}
                {SORT_PRESETS.every((preset) => preset.value !== Number(form.sort_order)) ? (
                  <option value={form.sort_order}>{vh.sortCustom}</option>
                ) : null}
              </Select>
              <p className="mt-1 text-xs text-secondary-500">{vh.sortHint}</p>
            </div>
          </div>

          <div>
            <Label>{vh.fieldSource}</Label>
            <SegmentedControl
              className="w-full [&>button]:flex-1"
              layoutId="video-source-pill"
              value={form.source_type}
              onChange={(value) => setForm((prev) => ({ ...prev, source_type: value }))}
              options={[
                { value: 'url', label: vh.sourceUrl },
                { value: 'file', label: vh.sourceFile },
              ]}
            />
          </div>

          {form.source_type === 'url' ? (
            <div>
              <Label htmlFor="video-url" required>{vh.fieldUrl}</Label>
              <Input
                id="video-url"
                value={form.external_url}
                placeholder="https://www.youtube.com/watch?v=..."
                onChange={(event) => setForm((prev) => ({ ...prev, external_url: event.target.value }))}
                error={Boolean(fieldErrors.external_url)}
              />
              {fieldErrors.external_url ? <p className="mt-1 text-xs text-error-500">{fieldErrors.external_url}</p> : null}
              <p className="mt-1 text-xs text-secondary-500">{vh.urlHint}</p>
            </div>
          ) : (
            <div>
              <Label>{vh.fieldFile}</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.mov,.ogg,.m4v"
                className="sr-only"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
              <Button className="mt-1 w-full max-w-full overflow-hidden sm:w-auto" variant="secondary" icon={ArrowUpTrayIcon} onClick={() => fileInputRef.current?.click()}>
                <span className="min-w-0 truncate">{file ? file.name : (editing?.original_filename || vh.chooseFile)}</span>
              </Button>
              <p className="mt-1 break-words text-xs text-secondary-500">{vh.fileHint}</p>
            </div>
          )}

          <div className="grid gap-5 lg:grid-cols-2">
          <div>
            <Label>{vh.fieldAudience}</Label>
            <SegmentedControl
              className="mt-2 w-full [&>button]:flex-1"
              layoutId="video-audience-pill"
              aria-label={vh.fieldAudience}
              value={audienceValue(form)}
              onChange={(value) => setForm((prev) => ({
                ...prev,
                visible_to_clients: value !== 'vendors',
                visible_to_vendors: value !== 'clients',
              }))}
              options={[
                { value: 'clients', label: vh.audienceClients },
                { value: 'vendors', label: vh.audienceVendors },
                { value: 'all', label: vh.audienceAll },
              ]}
            />
            <p className="mt-2 text-xs text-secondary-500">
              {audienceValue(form) === 'all'
                ? vh.audienceHintAll
                : audienceValue(form) === 'vendors'
                  ? vh.audienceVendorsHint
                  : vh.audienceClientsHint}
            </p>
            {fieldErrors.audience ? <p className="mt-1 text-xs text-error-500">{fieldErrors.audience}</p> : null}
          </div>

          <div>
            <Label>{vh.fieldListing}</Label>
            <SegmentedControl
              className="mt-2 w-full [&>button]:flex-1"
              layoutId="video-listing-pill"
              aria-label={vh.fieldListing}
              value={listingValue(form)}
              onChange={(value) => setForm((prev) => ({
                ...prev,
                is_published: value !== 'draft',
                is_featured: value === 'featured',
              }))}
              options={[
                { value: 'draft', label: vh.draft },
                { value: 'published', label: vh.published },
                { value: 'featured', label: vh.featured },
              ]}
            />
            <p className="mt-2 text-xs text-secondary-500">
              {listingValue(form) === 'featured'
                ? vh.featuredHint
                : listingValue(form) === 'draft'
                  ? vh.draftHint
                  : vh.publishedHint}
            </p>
          </div>
          </div>

          <div>
            <Label htmlFor="video-thumb">{vh.fieldThumbnail}</Label>
            <Input
              id="video-thumb"
              value={form.thumbnail_url}
              placeholder="https://..."
              onChange={(event) => setForm((prev) => ({ ...prev, thumbnail_url: event.target.value }))}
            />
            <input
              ref={thumbInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="sr-only"
              onChange={(event) => setThumbnailFile(event.target.files?.[0] || null)}
            />
            <Button className="mt-2 w-full max-w-full overflow-hidden min-h-[44px] sm:w-auto sm:min-h-0" variant="ghost" size="small" icon={ArrowUpTrayIcon} onClick={() => thumbInputRef.current?.click()}>
              <span className="min-w-0 truncate">{thumbnailFile ? thumbnailFile.name : vh.uploadThumbnail}</span>
            </Button>
          </div>

          <div className="space-y-3">
            <Label>{vh.fieldLinks}</Label>
            {form.links.map((link, index) => (
              <div key={`link-${index}`} className="grid gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
                <Input
                  value={link.label}
                  placeholder={vh.linkLabel}
                  onChange={(event) => updateLink(index, 'label', event.target.value)}
                />
                <Input
                  value={link.url}
                  placeholder="https://"
                  onChange={(event) => updateLink(index, 'url', event.target.value)}
                />
                <Button
                  className="min-h-[44px] w-full sm:min-h-0 sm:w-auto"
                  variant="ghost"
                  size="small"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    links: prev.links.filter((_, linkIndex) => linkIndex !== index),
                  }))}
                >
                  {vh.removeLink}
                </Button>
              </div>
            ))}
            <Button
              className="min-h-[44px] w-full sm:min-h-0 sm:w-auto"
              variant="secondary"
              size="small"
              icon={LinkIcon}
              onClick={() => setForm((prev) => ({ ...prev, links: [...prev.links, { label: '', url: '' }] }))}
            >
              {vh.addLink}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(previewVideo)}
        onClose={() => setPreviewVideo(null)}
        title={previewVideo?.title}
        size="xl"
      >
        {previewVideo ? (
          <div className="min-w-0">
            <VideoPlayer video={previewVideo} autoPlay />
          </div>
        ) : null}
      </Modal>
      {dialog}
    </div>
  );
}

export default VideoHubAdmin;
